import { useCallback, useEffect, useState, type ReactNode } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useCollectionBinding,
  useSlotGroup,
} from '@vibesites/runtime/react';
import {
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
  faArchive,
  faBanSmoking,
  faBasketball,
  faBath,
  faBed,
  faBellConcierge,
  faBicycle,
  faBolt,
  faBookOpen,
  faBox,
  faBoxesPacking,
  faBriefcase,
  faBuilding,
  faBuildingUser,
  faCalendarDays,
  faCar,
  faCarSide,
  faChess,
  faChild,
  faCity,
  faClipboardList,
  faComputer,
  faCouch,
  faCreditCard,
  faDoorOpen,
  faDroplet,
  faDumbbell,
  faElevator,
  faFan,
  faFileContract,
  faFire,
  faFireBurner,
  faGamepad,
  faGolfBallTee,
  faHandHoldingHeart,
  faHandshake,
  faHeartPulse,
  faHotel,
  faHotTubPerson,
  faHouse,
  faIceCream,
  faKitchenSet,
  faLandmark,
  faLanguage,
  faLeaf,
  faMartiniGlass,
  faMaximize,
  faMedal,
  faMoneyBillWave,
  faMountainSun,
  faMugHot,
  faPaintRoller,
  faPaw,
  faPersonSwimming,
  faPersonWalking,
  faPhone,
  faPrint,
  faRecycle,
  faRug,
  faRulerVertical,
  faSeedling,
  faShieldHalved,
  faShirt,
  faShower,
  faSink,
  faSnowflake,
  faSpa,
  faSquareParking,
  faStar,
  faStore,
  faTable,
  faTrainTram,
  faTree,
  faTruck,
  faTv,
  faUmbrellaBeach,
  faUserShield,
  faUsers,
  faUtensils,
  faVolleyball,
  faWarehouse,
  faWater,
  faWeightHanging,
  faWheelchair,
  faWifi,
  faWrench,
} from '@fortawesome/free-solid-svg-icons';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';

const ITEMS_PER_PAGE = 8;
const PAGE_INTERVAL_MS = 5000;

interface Amenity {
  title?: string | null;
  description?: string | null;
  group?: string | null;
  amenity_icon?: string | null;
  amenity_image?: string | null;
  amenity_image_alt_text?: string | null;
  is_featured?: string | null;
}

const ctaLinkDef: SlotGroupDefinition = {
  label: 'View All Amenities CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/amenities' },
    label: { label: 'Label', type: 'text', defaultValue: 'View All Amenities' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

type IconRule = { keywords: string[]; icon: IconDefinition };

/**
 * Ordered most-specific → least-specific. First match wins.
 * Covers common multifamily amenity phrasing; unknown titles fall back to faStar.
 */
const AMENITY_ICON_RULES: IconRule[] = [
  // Payments & online services
  { keywords: ['online service', 'service request', 'residents center', 'resident center'], icon: faClipboardList },
  { keywords: ['online payment', 'online rent', 'rent payment', 'pay rent', 'flexible rent payment'], icon: faCreditCard },
  { keywords: ['quick connect', 'high-speed internet', 'high speed internet', 'free internet', 'google fiber'], icon: faWifi },
  { keywords: ['atm'], icon: faMoneyBillWave },
  { keywords: ['energy efficient', 'energy star'], icon: faLeaf },

  // Maintenance & staff
  { keywords: ['emergency maintenance', '24-hour maintenance', '24 hour maintenance'], icon: faWrench },
  { keywords: ['on-site maintenance', 'onsite maintenance', 'maintenance'], icon: faWrench },
  { keywords: ['on-site management', 'onsite management', 'property management'], icon: faBuildingUser },
  { keywords: ['lobby attendant', '24-hour lobby', '24 hour lobby', 'doorman', 'front desk'], icon: faBellConcierge },
  { keywords: ['on-demand resident', 'on demand resident', 'resident services', 'spruce'], icon: faHandHoldingHeart },
  { keywords: ['courtesy patrol', 'security patrol'], icon: faUserShield },
  { keywords: ['bilingual'], icon: faLanguage },
  { keywords: ['concierge'], icon: faBellConcierge },
  { keywords: ['dry cleaning', 'dry clean'], icon: faShirt },
  { keywords: ['package receiving', 'package lock', 'package room', 'amazon hub', 'parcel'], icon: faBoxesPacking },

  // Programs & discounts
  { keywords: ['renters insurance', 'renter insurance', 'renter s insurance'], icon: faFileContract },
  { keywords: ['flexible lease', 'flexible lease term', 'lease term', 'short term lease', 'month to month'], icon: faFileContract },
  { keywords: ['employer discount', 'preferred employer'], icon: faHandshake },
  { keywords: ['military discount', 'active duty military', 'active duty'], icon: faMedal },
  { keywords: ['planned social activities', 'planned social', 'social activities', 'social activity', 'community event', 'social events'], icon: faCalendarDays },

  // Transit, location & building character
  { keywords: ['streetcar', 'trolley', 'light rail', 'transit stop', 'train stop'], icon: faTrainTram },
  { keywords: ['public library', 'library'], icon: faBookOpen },
  { keywords: ['power and light', 'power light district', 'district nearby'], icon: faCity },
  { keywords: ['historic building', 'historic'], icon: faLandmark },
  { keywords: ['loading dock', 'freight elevator'], icon: faTruck },
  { keywords: ['retail', 'restaurant space', 'restaurant'], icon: faUtensils },
  { keywords: ['market', 'convenience store', 'bakery'], icon: faStore },

  // Community spaces
  { keywords: ['business center', 'conference room', 'conference', 'co-work', 'cowork', 'co working', 'coworking', 'meeting room'], icon: faBriefcase },
  { keywords: ['coffee station', 'coffee bar'], icon: faMugHot },
  { keywords: ['game room', 'arcade'], icon: faGamepad },
  { keywords: ['guest suite', 'guest apartment', 'short-term rental', 'short term rental'], icon: faHotel },
  { keywords: ['resident lounge', 'sky lounge', 'penthouse lounge', 'club room'], icon: faCouch },
  { keywords: ['poolside clubhouse', 'clubhouse'], icon: faHouse },
  { keywords: ['playground'], icon: faChild },
  { keywords: ['grilling', 'grill', 'bbq', 'barbecue'], icon: faFireBurner },
  { keywords: ['lounge', 'bar'], icon: faMartiniGlass },

  // Recreation & wellness
  { keywords: ['pet spa', 'dog wash', 'pet wash', 'pet groom'], icon: faPaw },
  { keywords: ['hot tub'], icon: faHotTubPerson },
  { keywords: ['resort-inspired pool', 'resort inspired pool', 'resort pool', 'swimming pool', 'rooftop pool'], icon: faPersonSwimming },
  { keywords: ['pool', 'pond'], icon: faWater },
  { keywords: ['cardio machine', 'cardio equipment', 'cardio', 'peloton'], icon: faHeartPulse },
  { keywords: ['free weight', 'free weights'], icon: faWeightHanging },
  { keywords: ['weight machine', 'weight machines'], icon: faDumbbell },
  { keywords: ['fitness center', 'fitness', 'gym', 'workout'], icon: faDumbbell },
  { keywords: ['spa', 'yoga', 'meditation', 'pilates', 'sauna'], icon: faSpa },
  { keywords: ['golf'], icon: faGolfBallTee },
  { keywords: ['volleyball'], icon: faVolleyball },
  { keywords: ['basketball'], icon: faBasketball },
  { keywords: ['lawn game', 'lawn games', 'bocce', 'cornhole'], icon: faChess },
  { keywords: ['recycling', 'recycle'], icon: faRecycle },
  { keywords: ['bike', 'bicycle', 'cycling'], icon: faBicycle },

  // Grounds & outdoor
  { keywords: ['walking trail', 'walking trails', 'walking path', 'hiking trail', 'hiking trails'], icon: faPersonWalking },
  { keywords: ['car wash'], icon: faCarSide },
  { keywords: ['nature view', 'nature views', 'scenic view', 'scenic views'], icon: faMountainSun },
  { keywords: ['landscaping', 'landscaped', 'landscape', 'grounds', 'garden', 'courtyard'], icon: faTree },
  { keywords: ['sky vue', 'sky veranda', 'veranda', 'outdoor', 'patio', 'terrace', 'balcony', 'deck'], icon: faUmbrellaBeach },
  { keywords: ['roof deck', 'rooftop'], icon: faBuilding },

  // Pets & policies
  { keywords: ['dog park', 'pet play'], icon: faPaw },
  { keywords: ['pet', 'dog', 'cat', 'pets welcome'], icon: faPaw },
  { keywords: ['smoke-free', 'smoke free', 'non-smoking', 'nonsmoking'], icon: faBanSmoking },

  // Parking & access
  { keywords: ['garage parking', 'assigned garage', 'attached garage', 'garage'], icon: faWarehouse },
  { keywords: ['covered parking', 'parking'], icon: faSquareParking },
  { keywords: ['ev charging', 'electric car', 'charging station', 'car charging'], icon: faBolt },
  { keywords: ['dual entry', 'private entry', 'entry'], icon: faDoorOpen },
  { keywords: ['elevator'], icon: faElevator },

  // In-unit features
  { keywords: ['open concept layout', 'open concept', 'open layout', 'concept layout'], icon: faMaximize },
  { keywords: ['vinyl flooring', 'vinyl floor', 'vinyl plank', 'luxury vinyl'], icon: faRug },
  { keywords: ['air conditioning', 'a/c', 'a.c.', 'central air', 'central ac', 'climate control'], icon: faSnowflake },
  { keywords: ['fireplace', 'fire place', 'wood burning'], icon: faFire },
  { keywords: ['ceiling fan', 'ceiling fans'], icon: faFan },
  { keywords: ['washer', 'dryer', 'laundry', 'in-home wash'], icon: faShirt },
  { keywords: ['dishwasher'], icon: faUtensils },
  { keywords: ['microwave'], icon: faKitchenSet },
  { keywords: ['ice maker'], icon: faIceCream },
  { keywords: ['kitchen island'], icon: faKitchenSet },
  { keywords: ['stainless', 'appliance package', 'appliances'], icon: faKitchenSet },
  { keywords: ['granite', 'quartz', 'countertop', 'counter top'], icon: faTable },
  { keywords: ['walk-in closet', 'walk in closet', 'master closet', 'closet'], icon: faArchive },
  { keywords: ['pantry'], icon: faBox },
  { keywords: ['storage closet', 'storage'], icon: faBox },
  { keywords: ['double vanities', 'dual vanities', 'dual vanity', 'vanity'], icon: faSink },
  { keywords: ['sprinkler'], icon: faDroplet },
  { keywords: ['foot ceiling', 'high ceiling', 'vaulted ceiling', 'soaring ceiling', 'ft ceiling', '9 foot', '9 ft', 'ceiling'], icon: faRulerVertical },
  { keywords: ['crown molding', 'crown moulding'], icon: faPaintRoller },
  { keywords: ['tile shower', 'shower', 'glass enclosure'], icon: faShower },
  { keywords: ['bath', 'bathtub'], icon: faBath },
  { keywords: ['bedroom', 'bed'], icon: faBed },
  { keywords: ['smart home', 'wired for technology', 'technology', 'wifi', 'internet'], icon: faWifi },
  { keywords: ['cable', 'tv'], icon: faTv },

  // General fallbacks
  { keywords: ['security', 'gated', 'controlled access'], icon: faShieldHalved },
  { keywords: ['wheelchair', 'accessible', 'ada'], icon: faWheelchair },
  { keywords: ['print', 'copier'], icon: faPrint },
  { keywords: ['phone', 'call'], icon: faPhone },
  { keywords: ['payment', 'pay'], icon: faMoneyBillWave },
  { keywords: ['computer', 'cyber'], icon: faComputer },
  { keywords: ['car', 'valet'], icon: faCar },
  { keywords: ['event', 'entertainment'], icon: faUsers },
  { keywords: ['green', 'sustainable', 'eco', 'solar'], icon: faLeaf },
  { keywords: ['modern', 'industrial', 'decor', 'design'], icon: faBuilding },
  { keywords: ['plant', 'sprout', 'landscaping'], icon: faSeedling },
];

function normalizeAmenityText(value: string): string {
  return value
    .toLowerCase()
    .replace(/[*]/g, '')
    .replace(/['\u2019]/g, '')
    .replace(/[\u2011\u2013\u2014/&-]/g, ' ')
    .replace(/\bcloseets?\b/g, 'closet')
    .replace(/\s+/g, ' ')
    .trim();
}

function amenityKeywordMatches(haystack: string, keyword: string): boolean {
  const normalized = normalizeAmenityText(keyword);
  if (!normalized) return false;

  const words = normalized.split(' ');
  const lastWord = words.pop();
  if (!lastWord) return false;

  const prefix = words
    .map((word) => word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    .join('\\s+');
  const lastEscaped = lastWord.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const pattern = prefix
    ? `\\b${prefix}\\s+${lastEscaped}(?:s|es)?\\b`
    : `\\b${lastEscaped}(?:s|es)?\\b`;

  return new RegExp(pattern, 'i').test(haystack);
}

function resolveAmenityIcon(title: string, iconHint?: string | null): IconDefinition {
  const haystack = normalizeAmenityText(`${title} ${iconHint ?? ''}`);

  for (const rule of AMENITY_ICON_RULES) {
    if (rule.keywords.some((keyword) => amenityKeywordMatches(haystack, keyword))) {
      return rule.icon;
    }
  }

  return faStar;
}

interface AmenityGridItemProps {
  amenity: Amenity;
  index: number;
  placeholder?: boolean;
}

function AmenityGridItem({ amenity, index, placeholder = false }: AmenityGridItemProps) {
  const label = amenity.title?.trim() || `Amenity ${index + 1}`;
  const icon = resolveAmenityIcon(label, amenity.amenity_icon);

  return (
    <li
      className={`po-grid-item${placeholder ? ' po-grid-item--placeholder' : ''}`}
      aria-hidden={placeholder}
    >
      <div className="po-icon-circle" aria-hidden="true">
        <FontAwesomeIcon icon={icon} className="po-icon" />
      </div>
      <span className="po-amenity-label font-body">{label}</span>
    </li>
  );
}

function amenityHasSelectUnitsAsterisk(amenity: Amenity): boolean {
  return Boolean(amenity.title?.includes('*'));
}

interface PaginatedAmenityGridProps {
  amenities: Amenity[];
  heading: ReactNode;
}

function PaginatedAmenityGrid({ amenities, heading }: PaginatedAmenityGridProps) {
  const [page, setPage] = useState(0);
  const [pageAnimKey, setPageAnimKey] = useState(0);
  const [slideDir, setSlideDir] = useState<'next' | 'prev'>('next');
  const [paused, setPaused] = useState(false);
  const [reduceMotion, setReduceMotion] = useState(false);
  const showSelectUnitsDisclaimer = amenities.some(amenityHasSelectUnitsAsterisk);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const sync = () => setReduceMotion(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  useEffect(() => {
    setPage(0);
    setPageAnimKey((key) => key + 1);
  }, [amenities.length]);

  const totalPages = Math.max(1, Math.ceil(amenities.length / ITEMS_PER_PAGE));
  const safePage = Math.min(page, totalPages - 1);
  const showPagination = totalPages > 1;
  const currentItems = amenities.slice(
    safePage * ITEMS_PER_PAGE,
    (safePage + 1) * ITEMS_PER_PAGE,
  );

  const goToPage = useCallback(
    (nextPage: number) => {
      const clamped = Math.max(0, Math.min(totalPages - 1, nextPage));
      setSlideDir(clamped > safePage ? 'next' : 'prev');
      setPage(clamped);
      setPageAnimKey((key) => key + 1);
    },
    [safePage, totalPages],
  );

  const advancePage = useCallback(
    (dir: 1 | -1) => {
      goToPage((safePage + dir + totalPages) % totalPages);
    },
    [goToPage, safePage, totalPages],
  );

  useEffect(() => {
    if (!showPagination || paused || reduceMotion) return;
    const id = setInterval(() => advancePage(1), PAGE_INTERVAL_MS);
    return () => clearInterval(id);
  }, [advancePage, paused, reduceMotion, showPagination]);

  const paddedItems: (Amenity | null)[] = [...currentItems];
  while (paddedItems.length < ITEMS_PER_PAGE) {
    paddedItems.push(null);
  }

  return (
    <div
      className="po-amenities-panel"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <div className={`po-grid-header${showPagination ? ' po-grid-header--paginated' : ''}`}>
        <div className="po-grid-header-title">{heading}</div>
        {showPagination ? (
          <div className="po-pagination">
            <button
              type="button"
              className="po-pagination-btn"
              onClick={() => advancePage(-1)}
              aria-label="Previous amenities page"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <span className="po-pagination-info font-body">
              {safePage + 1} / {totalPages}
            </span>
            <button
              type="button"
              className="po-pagination-btn"
              onClick={() => advancePage(1)}
              aria-label="Next amenities page"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        ) : null}
      </div>

      <div className="po-grid-area">
        <div className="po-grid-viewport">
          <ul
            key={pageAnimKey}
            className={`po-grid po-grid--page po-grid--page-${slideDir}`}
            aria-label="Featured amenities"
          >
            {paddedItems.map((amenity, index) =>
              amenity ? (
                <AmenityGridItem
                  key={`${amenity.title ?? 'amenity'}-${safePage}-${index}`}
                  amenity={amenity}
                  index={safePage * ITEMS_PER_PAGE + index}
                />
              ) : (
                <AmenityGridItem
                  key={`placeholder-${safePage}-${index}`}
                  amenity={{ title: '\u00A0' }}
                  index={index}
                  placeholder
                />
              ),
            )}
          </ul>
        </div>
      </div>

      {showSelectUnitsDisclaimer ? (
        <p className="po-asterisk-disclaimer font-body">* select units only.</p>
      ) : null}
    </div>
  );
}

export function PropertyOverview() {
  const ctaLink = useSlotGroup('propertyoverview.cta_link', ctaLinkDef);

  const amenities = useCollectionBinding<Amenity>('propertyoverview.amenities', {
    collection: 'amenities',
  });

  useRevealStyles();
  useCtaSheenStyles();
  const blurbRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useScrollReveal<HTMLDivElement>();

  return (
    <section className="po-section">
      <div className="po-layout">
        <div className="po-blurb-column reveal-fade-up">
          <div ref={blurbRef} className="po-blurb-card">
            <RichTextContent
              path="propertyoverview.subtitle"
              as="p"
              className="po-subtitle font-body"
            />

            <RichTextContent
              path="propertyoverview.title"
              as="h2"
              className="po-title font-heading"
            />

            <div className="po-accent-bar" aria-hidden="true" />

            <RichTextContent
              path="propertyoverview.blurb"
              as="div"
              className="po-blurb font-body"
            />

            <EditableSlotGroup groupPath="propertyoverview.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/amenities'}
                target={ctaLink._target || '_self'}
                className="po-cta vantage-cta-sheen"
              >
                <span className="relative z-10">{ctaLink.label || 'View All Amenities'}</span>
              </a>
            </EditableSlotGroup>
          </div>
        </div>

        <div ref={gridRef} className="po-grid-column reveal-fade-up">
          {amenities.length > 0 ? (
            <PaginatedAmenityGrid
              amenities={amenities}
              heading={
                <RichTextContent
                  path="propertyoverview.grid_heading"
                  as="h3"
                  className="po-grid-heading font-heading"
                />
              }
            />
          ) : (
            <>
              <RichTextContent
                path="propertyoverview.grid_heading"
                as="h3"
                className="po-grid-heading font-heading"
              />
              <p className="po-empty font-body">Amenities will be displayed here.</p>
            </>
          )}
        </div>
      </div>

      <style>{`
        .po-section {
          position: relative;
          /* Own stacking context instead of a root-level z-index, so the inner
             z-2/z-3 layers stay contained and the primary-color section never
             out-stacks fixed third-party widgets (e.g. chatbots). The card's
             negative-margin overlap with the Hero above still works because the
             Hero isolates its own stack and this section paints later in the DOM. */
          isolation: isolate;
        }

        .po-layout {
          display: grid;
          grid-template-columns: 1fr;
          background: var(--vibe-color-primary);
        }

        .po-blurb-column {
          position: relative;
          z-index: 2;
          padding: 0 1.25rem;
        }

        .po-blurb-card {
          position: relative;
          z-index: 3;
          margin-top: -5rem;
          background: var(--vibe-color-background);
          padding: 2.25rem 1.75rem 2rem;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.14);
          text-align: center;
        }

        .po-subtitle {
          margin: 0 0 0.75rem;
          font-size: 1.05rem;
          line-height: 1.45;
          color: var(--vibe-color-text);
        }

        .po-subtitle [data-vibesite-richtext] strong,
        .po-subtitle [data-vibesite-richtext] b,
        .po-subtitle [data-vibesite-richtext] em,
        .po-subtitle [data-vibesite-richtext] [data-type="field-token"],
        .po-subtitle [data-type="field-token"],
        .po-subtitle strong,
        .po-subtitle em {
          color: var(--vibe-color-text) !important;
          font-style: normal;
          font-weight: 600;
          text-decoration: none;
        }

        .po-title {
          margin: 0 0 1rem;
          font-size: clamp(2rem, 4vw, 2.75rem);
          line-height: 1.05;
          font-weight: 700;
          color: var(--vibe-color-text);
          text-transform: none;
        }

        .po-accent-bar {
          width: 3.5rem;
          height: 0.35rem;
          margin: 0 auto 1.25rem;
          border-radius: 999px;
          background: var(--vibe-color-secondary);
        }

        .po-blurb {
          margin: 0 0 1.75rem;
          font-size: 0.95rem;
          line-height: 1.7;
          color: var(--vibe-color-text-muted);
        }

        .po-blurb p {
          margin: 0;
        }

        .po-cta {
          --vantage-cta-sheen-accent: var(--vibe-color-primary);
          --vantage-cta-sheen-on-accent: var(--vibe-color-text-on-primary);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.95rem 1.5rem;
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }

        .po-grid-column {
          background: var(--vibe-color-primary);
          padding: 2.5rem 1.25rem 2rem;
        }

        .po-amenities-panel {
          display: grid;
          grid-template-areas:
            "header"
            "grid"
            "disclaimer";
          grid-template-columns: minmax(0, 1fr);
          row-gap: 2rem;
          width: 100%;
          max-width: 44rem;
          margin-inline: auto;
        }

        .po-asterisk-disclaimer {
          grid-area: disclaimer;
          margin: -0.75rem 0 0;
          text-align: center;
          font-size: 0.75rem;
          font-weight: 500;
          letter-spacing: 0.04em;
          line-height: 1.4;
          color: var(--vibe-color-text-on-primary);
          opacity: 0.85;
        }

        .po-grid-header {
          grid-area: header;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 1.25rem;
          min-width: 0;
          text-align: center;
        }

        .po-grid-header-title {
          min-width: 0;
          max-width: 100%;
        }

        .po-grid-column > .po-grid-heading {
          margin-bottom: 2rem;
          text-align: center;
        }

        .po-grid-heading {
          margin: 0;
          text-align: center;
          font-size: clamp(1.05rem, 2.4vw, 1.65rem);
          font-weight: 700;
          letter-spacing: 0.14em;
          line-height: 1.2;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-primary);
        }

        .po-grid-area {
          grid-area: grid;
          --po-grid-row-height: 9.25rem;
          --po-grid-rows: 4;
          --po-grid-row-gap: 1.75rem;
        }

        .po-grid-viewport {
          overflow: hidden;
          min-height: calc(
            var(--po-grid-rows) * var(--po-grid-row-height) +
            (var(--po-grid-rows) - 1) * var(--po-grid-row-gap)
          );
        }

        .po-grid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: var(--po-grid-row-gap) 1.25rem;
          margin: 0;
          padding: 0;
          list-style: none;
        }

        .po-grid--page-next {
          animation: po-page-next 0.38s cubic-bezier(0.22, 1, 0.36, 1);
        }

        .po-grid--page-prev {
          animation: po-page-prev 0.38s cubic-bezier(0.22, 1, 0.36, 1);
        }

        @keyframes po-page-next {
          from {
            opacity: 0;
            transform: translateX(18px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes po-page-prev {
          from {
            opacity: 0;
            transform: translateX(-18px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .po-grid-item {
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          gap: 0.85rem;
          min-height: var(--po-grid-row-height);
        }

        .po-grid-item--placeholder {
          visibility: hidden;
          pointer-events: none;
        }

        .po-icon-circle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 4.5rem;
          height: 4.5rem;
          border-radius: 999px;
          background: var(--vibe-color-secondary);
        }

        .po-icon {
          width: 1.65rem;
          height: 1.65rem;
          color: var(--vibe-color-text-on-secondary);
        }

        .po-amenity-label {
          max-width: 9rem;
          font-size: 0.68rem;
          font-weight: 600;
          line-height: 1.35;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--vibe-color-text-on-primary);
        }

        .po-pagination {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 0.75rem;
          flex-shrink: 0;
          color: var(--vibe-color-text-on-primary);
        }

        .po-pagination-btn {
          width: 36px;
          height: 36px;
          padding: 0;
          border-radius: 50%;
          border: 2px solid var(--vibe-color-text-on-primary);
          background: transparent;
          color: var(--vibe-color-text-on-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.2s, color 0.2s, opacity 0.2s, transform 0.15s;
        }

        .po-pagination-btn:hover:not(:disabled) {
          background: var(--vibe-color-text-on-primary);
          color: var(--vibe-color-primary);
        }

        .po-pagination-btn:active:not(:disabled) {
          transform: scale(0.94);
        }

        .po-pagination-btn:disabled {
          opacity: 0.35;
          cursor: not-allowed;
        }

        .po-pagination-info {
          min-width: 3.25rem;
          text-align: center;
          font-size: 0.875rem;
          font-variant-numeric: tabular-nums;
          font-weight: 600;
        }

        .po-empty {
          margin: 0;
          text-align: center;
          color: var(--vibe-color-text-on-primary);
          opacity: 0.85;
        }

        /* Tablet only: the overview is a single full-width column here, so the
           -5rem overlap would pull the card up over the hero image (and its
           right-edge action rail). Sit the card flush below the hero instead;
           the overlap resumes at >=992px where the two-column layout keeps the
           card on the left, clear of the hero's right rail. */
        @media (min-width: 768px) and (max-width: 991.98px) {
          .po-blurb-card {
            margin-top: 2rem;
          }
        }

        @media (min-width: 768px) {
          .po-amenities-panel {
            row-gap: 2.25rem;
            max-width: 48rem;
          }

          .po-grid-area {
            --po-grid-rows: 2;
            --po-grid-row-gap: 2rem;
          }

          .po-grid {
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: var(--po-grid-row-gap) 1.5rem;
          }

          .po-blurb-card {
            padding: 2.75rem 2.25rem 2.35rem;
          }

          .po-grid-column {
            padding: 3rem 2rem 2.5rem;
          }
        }

        @media (min-width: 992px) {
          .po-layout {
            grid-template-columns: minmax(280px, 36%) minmax(0, 1fr);
            align-items: stretch;
          }

          .po-blurb-column {
            padding: 0 0 0 clamp(1.5rem, 3vw, 3rem);
          }

          .po-grid-column {
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 3.5rem clamp(1.5rem, 3vw, 3rem) 2.75rem;
          }

          .po-amenities-panel {
            max-width: min(48rem, 100%);
          }
        }

        /* Wide enough gray column: title centred, arrows in their own track */
        @media (min-width: 1200px) {
          .po-grid-header--paginated {
            display: grid;
            grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
            align-items: center;
            column-gap: 0.75rem;
            row-gap: 0;
          }

          .po-grid-header--paginated .po-grid-header-title {
            grid-column: 2;
            justify-self: center;
          }

          .po-grid-header--paginated .po-pagination {
            grid-column: 3;
            justify-self: end;
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .po-grid--page-next,
          .po-grid--page-prev {
            animation: none;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyOverview',
  displayName: 'Property Overview',
  tokens: {
    'propertyoverview.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue: '<p>Loft-Style <strong>Living with</strong> Resort-Style</p>',
    },
    'propertyoverview.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: 'Amenities',
    },
    'propertyoverview.blurb': {
      label: 'Blurb',
      type: 'richtext',
      defaultField: 'property_home_amenities_blurb',
      defaultValue:
        '<p>Discover resort-style amenities designed for comfort, connection, and everyday ease.</p>',
    },
    'propertyoverview.grid_heading': {
      label: 'Featured Grid Heading',
      type: 'richtext',
      defaultValue: 'Featured Amenities',
    },
  },
  slotGroups: {
    'propertyoverview.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'propertyoverview.amenities': {
      label: 'Featured Amenities',
      collection: 'amenities',
      description: 'Amenities displayed in the icon grid.',
    },
  },
};

(PropertyOverview as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyOverview;
